import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';

/**
 * Shown when an anonymous user clicks "Save Draft" on the wizard.
 * Creates an account, stores the JWT, and links the given draft to the new user.
 */
export default function RegisterToSaveModal({ draftId, prefillEmail, prefillName, prefillCompany, onClose, onSaved }) {
  const [email, setEmail] = useState(prefillEmail || '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(prefillName || '');
  const [companyName, setCompanyName] = useState(prefillCompany || '');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    if (!email.trim() || !password || password.length < 6) {
      setError('Email and a password of at least 6 characters are required.');
      return;
    }
    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Register (backend returns access_token)
      const regRes = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          password,
          full_name: fullName.trim() || email.trim(),
          company_name: companyName.trim(),
          role: 'customer',
        }),
      });
      if (!regRes.ok) {
        const err = await regRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Registration failed');
      }
      const regData = await regRes.json();
      // Persist token + user so subsequent API calls are authenticated
      if (regData.access_token) {
        localStorage.setItem('auth_token', regData.access_token);
      }
      if (regData.user) {
        localStorage.setItem('user', JSON.stringify(regData.user));
      }
      // Link the in-progress draft to this new user
      if (draftId) {
        await api.request(`/assessments/${draftId}/link-user`, { method: 'POST' }).catch(() => {});
      }
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="register-to-save-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-gray-900">Save your progress</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-register-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-5">
          Create a free account so you can resume this assessment later from your dashboard.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Email *</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
              data-testid="register-email-input"
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Password *</label>
            <input type="password" required minLength={6} value={password} onChange={e => setPassword(e.target.value)}
              data-testid="register-password-input"
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Your Name</label>
            <input value={fullName} onChange={e => setFullName(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Company</label>
            <input value={companyName} onChange={e => setCompanyName(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={submitting}
            data-testid="register-and-save-btn"
            className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors">
            {submitting ? 'Saving...' : 'Create account & save draft'}
          </button>
        </form>
      </div>
    </div>
  );
}
